import { useListContext, RecordContextProvider, useTranslate } from 'react-admin';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import SimpleList from '@/components/global/list/SimpleList.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton.jsx';
import ArrowUpCollapseIcon from '@/assets/icons/arrow-up-collapse-icon.svg?react';
import ArrowDownCollapseIcon from '@/assets/icons/arrow-down-collapse-icon.svg?react';
import { activityStyles } from '@/themes/commonStyles/activity.js';
import ActivityListItem from '@/components/service/activity/ActivityListItem.jsx';
import useLoadMoreList from '@/composables/useLoadMoreList.js';

const ActivityListItems = ({ onLoadMore, onShowLess, perPage, initialPerPage = 5, resource, elementId }) => {
    const { data, isLoading, total } = useListContext();
    const [activeCommentId, setActiveCommentId] = useState(null);
    const translate = useTranslate();
    const hasMore = total === undefined ? data?.length === perPage : perPage < total;
    const canShowLess = perPage > initialPerPage;

    if (isLoading) {
        return (
            <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return <Typography>{translate('common.noData')}</Typography>;
    }

    const handleToggleActions = id => {
        setActiveCommentId(prev => (prev === id ? null : id));
    };

    return (
        <Box>
            {data.map(record => (
                <RecordContextProvider key={record.id} value={record}>
                    <ActivityListItem activeCommentId={activeCommentId} onToggleActions={handleToggleActions} resource={resource} elementId={elementId} />
                </RecordContextProvider>
            ))}
            <Box sx={activityStyles.activityLoadMore}>
                <Box className='activity-list-load-more'>
                    {canShowLess && (
                        <CustomButton
                            color='secondary'
                            size='small'
                            onClick={onShowLess}
                            label={translate('common.showLess')}
                            endIcon={<ArrowDownCollapseIcon />}
                        />
                    )}
                    {hasMore && (
                        <CustomButton
                            color='secondary'
                            size='small'
                            onClick={onLoadMore}
                            label={translate('common.showOlder')}
                            endIcon={<ArrowUpCollapseIcon />}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const ActivityList = ({ elementId, resource }) => {
    const translate = useTranslate();
    const initialPerPage = 5;
    const { perPage, handleLoadMore, handleShowLess } = useLoadMoreList(initialPerPage, 5);

    return (
        <SimpleList
            resource={`${resource}/${elementId}/change`}
            sort={{ field: 'createdAt', order: 'DESC' }}
            bulkActionButtons={false}
            disablePagination={true}
            perPage={perPage}
            paginationClassName='activity-list-pagination'
            empty={<Typography>{translate('common.noData')}</Typography>}
        >
            <ActivityListItems
                onLoadMore={handleLoadMore}
                onShowLess={handleShowLess}
                perPage={perPage}
                initialPerPage={initialPerPage}
                resource={resource}
                elementId={elementId}
            />
        </SimpleList>
    );
};

ActivityListItems.propTypes = {
    onLoadMore: PropTypes.func.isRequired,
    onShowLess: PropTypes.func.isRequired,
    perPage: PropTypes.number.isRequired,
    initialPerPage: PropTypes.number,
    resource: PropTypes.string.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

ActivityList.propTypes = {
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    resource: PropTypes.string.isRequired,
};

export default ActivityList;
